import React, { useState, useEffect, useRef } from 'react';
import { Star, StarHalf, Home } from 'lucide-react';
import { Link, useNavigate } from 'react-router-dom';
import { useReviews } from '../hooks/useReviews';

interface TooltipProps {
  text: string;
  children: React.ReactNode;
  maxLength: number;
  label?: string;
}

const Tooltip: React.FC<TooltipProps> = ({ text, children, maxLength, label }) => {
  const [showTooltip, setShowTooltip] = useState(false);
  const [tooltipPosition, setTooltipPosition] = useState({ x: 0, y: 0 });
  const tooltipRef = useRef<HTMLDivElement>(null);

  const handleMouseEnter = (e: React.MouseEvent) => {
    if (text && text.length > maxLength) {
      const rect = e.currentTarget.getBoundingClientRect();
      setTooltipPosition({
        x: rect.left + rect.width / 2,
        y: rect.top - 10
      });
      setShowTooltip(true);
    }
  };

  const handleMouseLeave = () => {
    setShowTooltip(false);
  };

  return (
    <div className="relative inline">
      <div
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
        className={text && text.length > maxLength ? 'cursor-help' : ''}
      >
        {children}
      </div>
      {showTooltip && text && text.length > maxLength && (
        <div
          ref={tooltipRef}
          className="fixed z-50 bg-white text-black p-3 rounded-lg shadow-lg max-w-sm text-sm border border-black"
          style={{
            left: `${tooltipPosition.x}px`,
            top: `${tooltipPosition.y}px`,
            transform: 'translate(-50%, -100%)',
            backgroundColor: '#f7faf7',
            color: '#4b5563'
          }}
        >
          {label && <span className="font-bold">{label}: </span>}
          {text}
          <div
            className="absolute top-full left-1/2 transform -translate-x-1/2 w-0 h-0 border-l-4 border-r-4 border-t-4 border-transparent"
            style={{ filter: 'drop-shadow(0 1px 0 black)' }}
          >
            <div 
              className="absolute -top-1 left-1/2 transform -translate-x-1/2 w-0 h-0 border-l-4 border-r-4 border-t-4 border-transparent"
              style={{ borderTopColor: '#f7faf7' }}
            />
          </div>
        </div>
      )}
    </div>
  );
};

const RecentReviews: React.FC = () => {
  const { reviews, loading, error } = useReviews();
  const navigate = useNavigate();
  const reviewsContainerRef = useRef<HTMLDivElement>(null);
  const [isScrollingPaused, setIsScrollingPaused] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const animationFrameRef = useRef<number | null>(null);
  const firstSetHeightRef = useRef<number>(0);
  const lastScrollTimeRef = useRef<number>(0);

  // Limit to first 6 reviews for better mobile performance
  const displayReviews = reviews.slice(0, 999);

  // Check if device is mobile
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 768); // md breakpoint
    };
    
    checkMobile();
    window.addEventListener('resize', checkMobile);
    
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  // Auto-scrolling effect using requestAnimationFrame
  useEffect(() => {
    if (isMobile || isScrollingPaused || !reviewsContainerRef.current || displayReviews.length === 0) {
      return;
    }

    // Calculate the exact height of the first set of reviews
    const container = reviewsContainerRef.current;
    const reviewElements = container.children;
    const firstSetLength = displayReviews.length;

    if (reviewElements.length >= firstSetLength) {
      // Get the offsetTop of the first duplicated review (which comes after the original set)
      const firstDuplicateElement = reviewElements[firstSetLength] as HTMLElement;
      firstSetHeightRef.current = firstDuplicateElement.offsetTop;
    }

    const scrollStep = (timestamp: number) => {
      if (!reviewsContainerRef.current || isScrollingPaused) {
        return;
      }

      const container = reviewsContainerRef.current;
      const currentScrollTop = container.scrollTop;

      // Only scroll if enough time has passed (throttle to ~60fps equivalent)
      if (timestamp - lastScrollTimeRef.current >= 32) {
        // Reset scroll position when we've scrolled past the first set of reviews
        if (currentScrollTop >= firstSetHeightRef.current) {
          container.scrollTop = 0;
        } else {
          // Scroll down by 1 pixel for smooth scrolling
          container.scrollTop += 1;
        }
        lastScrollTimeRef.current = timestamp;
      }

      // Continue the animation loop
      animationFrameRef.current = requestAnimationFrame(scrollStep);
    };

    // Start the animation loop
    animationFrameRef.current = requestAnimationFrame(scrollStep);

    return () => {
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current);
      }
    };
  }, [isScrollingPaused, displayReviews]);

  const handleMouseEnter = () => {
    setIsScrollingPaused(true);
  };

  const handleMouseLeave = () => {
    setIsScrollingPaused(false);
  };

  const renderStars = (rating: number, size: 'sm' | 'lg' = 'sm') => {
    const circleSize = size === 'lg' ? 'w-6 h-6' : 'w-4 h-4';
    const starSize = size === 'lg' ? 'w-4 h-4' : 'w-3 h-3';
    
    const getStarColor = (starRating: number) => {
      if (starRating >= 4.2) return '#008d49'; // 5 - dark green
      if (starRating >= 3.4) return '#a1e883'; // 4 - light green  
      if (starRating >= 2.6) return '#ffd830'; // 3 - yellow
      if (starRating >= 1.8) return '#f86306'; // 2 - orange
      return '#ef001d'; // 1 - red
    };
    
    return (
      <div className="flex">
        {[0, 1, 2, 3, 4].map((index) => {
          const filled = rating - index;
          const starColor = getStarColor(rating);
          return (
            <div key={index} className={`${circleSize} rounded-full flex items-center justify-center`}
                 style={{ backgroundColor: filled >= 1 ? starColor : (filled > 0 ? starColor : '#d1d5db') }}>
              {filled >= 1 ? (
                <Star className={`${starSize} text-white fill-white`} />
              ) : filled > 0 ? (
                <StarHalf className={`${starSize} text-white fill-white`} />
              ) : (
                <Star className={`${starSize} text-white fill-white opacity-60`} />
              )}
            </div>
          );
        })}
      </div>
    );
  };

  const getRatingIcon = (rating: number, category: string) => {
    const getColor = () => {
      const roundedRating = Math.round(rating);
      if (roundedRating === 5) return '#008d49';
      if (roundedRating === 4) return '#a1e883';
      if (roundedRating === 3) return '#ffd830';
      if (roundedRating === 2) return '#f86306';
      return '#ef001d'; // For rating 1
    };

    const colorValue = getColor();

    return (
      <div className="relative w-6 h-6 flex items-center justify-center">
        <Star 
          className="w-6 h-6 text-white"
          style={{ fill: colorValue, color: colorValue }}
        />
        <span className="absolute inset-0 flex items-center justify-center text-white text-xs font-bold z-10">
          {Math.round(rating)}
        </span>
      </div>
    );
  };

  const calculateOverallRating = (review: any) => {
    const ratings = [
      review.property_rating,
      review.location_rating,
      ...(review.agent_rating > 0 ? [review.agent_rating] : []),
      ...(review.landlord_rating > 0 ? [review.landlord_rating] : [])
    ];
    return ratings.reduce((sum, rating) => sum + rating, 0) / ratings.length;
  };

  const truncateText = (text: string, maxLength: number = 120) => {
    if (!text) return '';
    return text.length > maxLength ? text.substring(0, maxLength) + '...' : text;
  };

  const extractCityFromAddress = (address: string) => {
    if (!address) return 'Unknown';
    
    // Split address by commas and get the second to last part (before postcode)
    const parts = address.split(',').map(part => part.trim());
    
    // If we have at least 2 parts, the city is typically the second to last
    if (parts.length >= 2) {
      const cityPart = parts[parts.length - 2];
      // Remove any postcode patterns from the city part
      const cityWithoutPostcode = cityPart.replace(/\b[A-Z]{1,2}\d{1,2}[A-Z]?\s*\d[A-Z]{2}\b/g, '').trim();
      return cityWithoutPostcode || cityPart;
    }
    
    // Fallback to the first part if we can't determine the city
    return parts[0] || 'Unknown';
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#30a86b]"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="text-center text-red-600 p-4">
        Error loading reviews: {error}
      </div>
    );
  }

  if (reviews.length === 0) {
    return (
      <div className="text-center text-gray-500 p-4">
        No reviews available yet.
      </div>
    );
  }

  const ReviewCard = ({ review }: { review: any }) => {
    const overallRating = calculateOverallRating(review);
    
    const handleImageClick = () => {
      try {
        const propertyUrl = `/property/${encodeURIComponent(review.address)}${review.udprn ? `?udprn=${review.udprn}` : ''}`;
        console.log('Navigating to:', propertyUrl); // Debug log
        navigate(propertyUrl);
      } catch (error) {
        console.error('Navigation error:', error);
        // Fallback to window.location if navigate fails
        window.location.href = `/property/${encodeURIComponent(review.address)}${review.udprn ? `?udprn=${review.udprn}` : ''}`;
      }
    };
    
    return (
      <div className="bg-white rounded-2xl shadow-lg overflow-hidden mb-4">
        {/* Black Header */}
        <div className="bg-black text-white p-4 flex items-start gap-3 min-w-0">
          <div className="w-10 h-10 rounded-full flex items-center justify-center" style={{ backgroundColor: '#32a86c' }}>
            <Home className="w-5 h-5 text-white" />
          </div>
          <div className="flex-1 min-w-0">
            <h3 className="font-bold text-sm leading-tight break-words">{review.address}</h3>
          </div>
        </div>

        {/* Main Content */}
        <div className="p-4">
          {/* Rating and Read Review Button */}
          <div className="flex flex-col md:flex-row items-center justify-between mb-4 gap-3 md:gap-0">
            <Link 
              to={`/property/${encodeURIComponent(review.address)}${review.udprn ? `?udprn=${review.udprn}` : ''}`}
              className="text-white px-3 py-1.5 md:px-4 md:py-2 rounded-full text-xs md:text-sm font-medium transition-colors order-2 md:order-1"
              style={{ backgroundColor: '#32a86c' }}
              onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#2a9760'}
              onMouseLeave={(e) => e.currentTarget.style.backgroundColor = '#32a86c'}
            >
              Read full review
            </Link>
            <div className="flex items-center gap-3 order-1 md:order-2">
              <span className="text-4xl font-bold">{overallRating.toFixed(1)}</span>
              {renderStars(overallRating, 'lg')}
            </div>
          </div>

          {/* Rating Categories */}
          <div className="space-y-3 mb-4">
            <div className="flex items-center gap-3">
              {getRatingIcon(review.property_rating, 'property')}
              <div className="flex-1">
                <span className="font-semibold text-sm">Property: </span>
                <Tooltip text={review.property_review || ''} maxLength={120} label="Property">
                  <span className="text-sm text-gray-700">
                    {truncateText(review.property_review)}
                  </span>
                </Tooltip>
              </div>
            </div>

            {review.agent_rating > 0 && (
              <div className="flex items-center gap-3">
                {getRatingIcon(review.agent_rating, 'agent')}
                <div className="flex-1">
                  <span className="font-semibold text-sm">Letting Agent: </span>
                  <Tooltip text={review.agent_review || ''} maxLength={120} label="Letting Agent">
                    <span className="text-sm text-gray-700">
                      {truncateText(review.agent_review)}
                    </span>
                  </Tooltip>
                </div>
              </div>
            )}

            <div className="flex items-center gap-3">
              {getRatingIcon(review.location_rating, 'location')}
              <div className="flex-1">
                <span className="font-semibold text-sm">Location: </span>
                <Tooltip text={review.location_review || ''} maxLength={120} label="Location">
                  <span className="text-sm text-gray-700">
                    {truncateText(review.location_review)}
                  </span>
                </Tooltip>
              </div>
            </div>

            {review.landlord_rating > 0 && (
              <div className="flex items-center gap-3">
                {getRatingIcon(review.landlord_rating, 'landlord')}
                <div className="flex-1">
                  <span className="font-semibold text-sm">
                    Landlord
                    {(() => {
                      switch (review.property_owner) {
                        case 'private-individual':
                          return ' (Private Landlord)';
                        case 'private-company':
                          return ' (Private Company)';
                        case 'housing-association':
                          return ' (Housing Association)';
                        case 'council':
                          return ' (Council)';
                        case 'student-accommodation':
                          return ' (Student Accommodation)';
                        case 'unknown':
                          return ' (Unknown)';
                        default:
                          return '';
                      }
                    })()}
                    : 
                  </span>
                  <Tooltip text={review.landlord_review || ''} maxLength={120} label="Landlord">
                    <span className="text-sm text-gray-700">
                      {truncateText(review.landlord_review)}
                    </span>
                  </Tooltip>
                </div>
              </div>
            )}
          </div>

          {/* Images */}
          {review.images && review.images.length > 0 && review.ai_images_status !== 'fail' && (
            <div className="flex gap-2 mb-4">
              {review.images.slice(0, 2).map((url: string, index: number) => (
                <div
                  key={index}
                  onClick={handleImageClick}
                  className="focus:outline-none hover:opacity-90 transition-opacity cursor-pointer"
                  role="button"
                  tabIndex={0}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault();
                      handleImageClick();
                    }
                  }}
                >
                  <img
                    src={url}
                    alt={`Property image ${index + 1}`}
                    className="w-20 h-16 object-cover rounded-lg cursor-pointer pointer-events-none"
                  />
                </div>
              ))}
            </div>
          )}

          {/* Footer Info */}
          <div className="flex items-center justify-between pt-3 border-t border-gray-400">
            <div className="space-y-1">
              <div className="text-sm">
                <span className="font-semibold">Ownership: </span>
                <span className="text-gray-600">
                  {review.property_owner === 'private-individual' && 'Landlord'}
                  {review.property_owner === 'private-company' && (review.private_company_name || 'Private Company')}
                  {review.property_owner === 'student-accommodation' && (review.student_accommodation_name || 'Student Accommodation')}
                  {review.property_owner === 'housing-association' && (review.council_housing_name || 'Housing Association')}
                  {review.property_owner === 'council' && (review.council_housing_name || 'Council')}
                  {review.property_owner === 'unknown' && 'Unknown'}
                  {!review.property_owner && 'Not specified'}
                </span>
              </div>
              {review.letting_agent_name && (
                <div className="text-sm">
                  <span className="font-semibold">Letting Agent: </span>
                  <span className="text-gray-600">{review.letting_agent_name}</span>
                </div>
              )}
            </div>
            <div className="flex items-center gap-1 ml-1 pr-4" style={{ color: '#ef001d' }}>
              <img 
                src="https://rentreview.io/wp-content/uploads/2025/08/Icon-pin.png" 
                alt="Location pin" 
                className="w-4 h-4"
              />
              <span className="text-sm font-semibold" style={{ color: '#ef001d' }}>{extractCityFromAddress(review.address)}</span>
            </div>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div 
      ref={reviewsContainerRef}
      className={`h-[600px] overflow-y-auto space-y-4 pr-2 ${
        isMobile 
          ? 'scrollbar-visible border-2 border-gray-300 rounded-lg p-2' 
          : 'scrollbar-thin'
      }`}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
    >
      {/* Original set of reviews */}
      {displayReviews.map((review) => (
        <ReviewCard key={review.id} review={review} />
      ))}
      
      {/* Duplicate the reviews for seamless looping */}
      {displayReviews.map((review) => (
        <ReviewCard key={`duplicate-${review.id}`} review={review} />
      ))}
    </div>
  );
};

export default RecentReviews;